import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UploadCloud, Trash2, RefreshCw, Download, Ban } from "lucide-react";

function UploadZone({ label, onUpload, testid }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(file);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-dashed border-zinc-300 bg-zinc-50 p-6 rounded-sm text-center" data-testid={testid}>
      <UploadCloud size={22} className="mx-auto text-primary mb-2" />
      <div className="font-display font-bold">{label}</div>
      <div className="text-xs text-muted-foreground">CSV or XLSX. Columns: <span className="font-mono">email</span> &amp; <span className="font-mono">domain</span></div>
      <input
        ref={inputRef} type="file" accept=".csv,.xlsx"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="mt-3 text-sm"
        data-testid={`${testid}-input`}
      />
      <div className="mt-3">
        <Button onClick={submit} disabled={!file || busy} className="rounded-sm h-9 bg-primary hover:bg-[#00227A]" data-testid={`${testid}-button`}>
          {busy ? "Uploading…" : "Upload"}
        </Button>
      </div>
      {file && <div className="text-xs font-mono mt-2">{file.name} · {Math.max(1, Math.round(file.size / 1024))} KB</div>}
    </div>
  );
}

function ExclusionTable({ items, onDelete, scope }) {
  return (
    <div className="border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40">
          <tr className="text-left border-b border-border">
            {scope === "campaign" && <th className="px-3 py-2 meta-label">Campaign</th>}
            <th className="px-3 py-2 meta-label">Email</th>
            <th className="px-3 py-2 meta-label">Domain</th>
            <th className="px-3 py-2 meta-label">Source</th>
            <th className="px-3 py-2 meta-label">Added By</th>
            <th className="px-3 py-2 meta-label">Added At</th>
            <th className="px-3 py-2 meta-label text-right"></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No entries.</td></tr>
          )}
          {items.map((e) => (
            <tr key={e.id} className="border-b border-border last:border-0">
              {scope === "campaign" && <td className="px-3 py-2 whitespace-nowrap">{e.campaign_name}</td>}
              <td className="px-3 py-2 font-mono text-xs">{e.email || "—"}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.domain || "—"}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.source || "—"}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.added_by || "—"}</td>
              <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{(e.added_at || "").slice(0, 19).replace("T", " ")}</td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => onDelete(e.id)} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminExclusionLists() {
  const { options } = useAuth();
  const campaigns = options?.campaigns ?? [];
  const [masterItems, setMasterItems] = useState([]);
  const [masterTotal, setMasterTotal] = useState(0);
  const [campItems, setCampItems] = useState([]);
  const [campTotal, setCampTotal] = useState(0);
  const [campFilter, setCampFilter] = useState("");
  const [campTarget, setCampTarget] = useState("");
  // DNC state — parallel to the exclusion state. DNC is a SEPARATE
  // database from Campaign/Master Exclusions (see /api/dnc/* endpoints).
  const [masterDncTotal, setMasterDncTotal] = useState(0);
  const [campDncTotal, setCampDncTotal] = useState(0);
  const [campDncFilter, setCampDncFilter] = useState("");
  const [campDncTarget, setCampDncTarget] = useState("");

  // Since the on-page list/table was removed, we only need the TOTAL count.
  // Use limit=1 so the fetch is fast even with 250K+ exclusion entries — the
  // total still comes back via `count_documents`.
  const loadMaster = async () => {
    const { data } = await api.get("/exclusions/master", { params: { limit: 1 }, timeout: 30000 });
    setMasterItems(data.items); setMasterTotal(data.total);
  };
  const loadCampaign = async () => {
    const { data } = await api.get("/exclusions/campaign", {
      params: { campaign: campFilter || undefined, limit: 1 },
      timeout: 30000,
    });
    setCampItems(data.items); setCampTotal(data.total);
  };
  useEffect(() => { loadMaster(); }, []);
  useEffect(() => { loadCampaign(); /* eslint-disable-next-line */ }, [campFilter]);

  // DNC loaders mirror loadMaster/loadCampaign exactly (count-only fetch).
  const loadMasterDnc = async () => {
    const { data } = await api.get("/dnc/master", { params: { limit: 1 }, timeout: 30000 });
    setMasterDncTotal(data.total);
  };
  const loadCampaignDnc = async () => {
    const { data } = await api.get("/dnc/campaign", {
      params: { campaign: campDncFilter || undefined, limit: 1 },
      timeout: 30000,
    });
    setCampDncTotal(data.total);
  };
  useEffect(() => { loadMasterDnc(); }, []);
  useEffect(() => { loadCampaignDnc(); /* eslint-disable-next-line */ }, [campDncFilter]);

  // Real-time poll: re-fetch DNC counts every 10s while the tab is visible
  // so any Add from the Add Lead form surfaces here without a manual refresh.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      loadMasterDnc();
      loadCampaignDnc();
      // Also refresh the exclusion counts so the whole tab feels live.
      loadMaster();
      loadCampaign();
    };
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [campDncFilter, campFilter]);

  const uploadMasterDnc = async (file) => {
    let data;
    try {
      const fd = new FormData(); fd.append("file", file);
      const resp = await api.post("/dnc/master/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      data = resp.data;
      toast.success(`Inserted ${data.inserted} Master DNC entr${data.inserted === 1 ? "y" : "ies"}`);
    } catch (e) {
      console.error("[Master DNC upload] failed", e);
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : (e?.message || "Upload failed"));
      return;
    }
    try { await loadMasterDnc(); }
    catch (e) { if (data?.inserted) setMasterDncTotal((t) => t + data.inserted); }
  };
  const uploadCampaignDnc = async (file) => {
    if (!campDncTarget) return toast.error("Pick a campaign first.");
    let data;
    try {
      const fd = new FormData(); fd.append("file", file);
      const resp = await api.post(`/dnc/campaign/upload?campaign_name=${encodeURIComponent(campDncTarget)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      data = resp.data;
      toast.success(`Inserted ${data.inserted} DNC entr${data.inserted === 1 ? "y" : "ies"} for ${data.campaign_name}`);
    } catch (e) {
      console.error("[Campaign DNC upload] failed", e);
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : (e?.message || "Upload failed"));
      return;
    }
    try {
      setCampDncFilter(campDncTarget);
      await loadCampaignDnc();
    } catch (e) { if (data?.inserted) setCampDncTotal((t) => t + data.inserted); }
  };
  const exportMasterDnc = async (fmt) => {
    try {
      const res = await api.get("/dnc/master/export", { params: { fmt }, responseType: "blob" });
      downloadBlob(res.data, `master_dnc.${fmt}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const exportCampaignDnc = async (fmt) => {
    try {
      const res = await api.get("/dnc/campaign/export", {
        params: { fmt, campaign: campDncFilter || undefined }, responseType: "blob",
      });
      const base = `campaign_dnc_${campDncFilter || "all"}`.replace(/\s+/g, "_");
      downloadBlob(res.data, `${base}.${fmt}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const purgeMasterDnc = async () => {
    if (!window.confirm(`Permanently delete ALL ${masterDncTotal} Master DNC entries? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/dnc/master");
      toast.success(`Deleted ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}`);
      await loadMasterDnc();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const purgeCampaignDnc = async () => {
    const what = campDncFilter ? `for campaign "${campDncFilter}"` : "across ALL campaigns";
    if (!window.confirm(`Permanently delete ALL ${campDncTotal} Campaign DNC entries ${what}? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/dnc/campaign", {
        params: { campaign: campDncFilter || undefined },
      });
      toast.success(`Deleted ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}`);
      await loadCampaignDnc();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadMaster = async (file) => {
    let data;
    try {
      const fd = new FormData(); fd.append("file", file);
      const resp = await api.post("/exclusions/master/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,  // large files can take a while to parse + insert
      });
      data = resp.data;
      toast.success(`Inserted ${data.inserted} master exclusion${data.inserted === 1 ? "" : "s"}`);
    } catch (e) {
      console.error("[Master upload] failed", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail
        ? formatApiErrorDetail(detail)
        : (status ? `Upload failed (HTTP ${status}). ${e?.message || ""}`.trim() : `Upload failed: ${e?.message || "network error"}`);
      toast.error(msg);
      return;
    }
    // Refresh is best-effort — failing to refresh must NOT masquerade as an
    // upload failure. Previously, a slow /exclusions/master fetch (262K+ rows)
    // would throw and the catch above would fire, hiding the success.
    try {
      await loadMaster();
    } catch (e) {
      console.warn("[Master upload] post-insert refresh failed", e?.message);
      // Bump the displayed total by `inserted` so the count is right without
      // a full re-fetch.
      if (data?.inserted) setMasterTotal((t) => t + data.inserted);
    }
  };
  const uploadCampaign = async (file) => {
    if (!campTarget) return toast.error("Pick a campaign first.");
    let data;
    try {
      const fd = new FormData(); fd.append("file", file);
      const resp = await api.post(`/exclusions/campaign/upload?campaign_name=${encodeURIComponent(campTarget)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      data = resp.data;
      toast.success(`Inserted ${data.inserted} entries for ${data.campaign_name}`);
    } catch (e) {
      console.error("[Campaign upload] failed", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail
        ? formatApiErrorDetail(detail)
        : (status ? `Upload failed (HTTP ${status}). ${e?.message || ""}`.trim() : `Upload failed: ${e?.message || "network error"}`);
      toast.error(msg);
      return;
    }
    // Best-effort refresh (see uploadMaster comment).
    try {
      setCampFilter(campTarget);
      await loadCampaign();
    } catch (e) {
      console.warn("[Campaign upload] post-insert refresh failed", e?.message);
      if (data?.inserted) setCampTotal((t) => t + data.inserted);
    }
  };

  const delMaster = async (id) => {
    if (!window.confirm("Remove this exclusion entry?")) return;
    try { await api.delete(`/exclusions/master/${id}`); await loadMaster(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delCamp = async (id) => {
    if (!window.confirm("Remove this exclusion entry?")) return;
    try { await api.delete(`/exclusions/campaign/${id}`); await loadCampaign(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const exportMaster = async (fmt) => {
    try {
      const res = await api.get("/exclusions/master/export", { params: { fmt }, responseType: "blob" });
      downloadBlob(res.data, `master_exclusions.${fmt}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const exportCamp = async (fmt) => {
    try {
      const res = await api.get("/exclusions/campaign/export", {
        params: { fmt, campaign: campFilter || undefined }, responseType: "blob",
      });
      const base = `campaign_exclusions_${campFilter || "all"}`.replace(/\s+/g, "_");
      downloadBlob(res.data, `${base}.${fmt}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const purgeMaster = async () => {
    if (!window.confirm(`Permanently delete ALL ${masterTotal} master exclusion entries? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/exclusions/master");
      toast.success(`Deleted ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}`);
      await loadMaster();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const purgeCamp = async () => {
    const what = campFilter ? `for campaign "${campFilter}"` : "across ALL campaigns";
    if (!window.confirm(`Permanently delete ALL ${campTotal} campaign exclusion entries ${what}? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/exclusions/campaign", {
        params: { campaign: campFilter || undefined },
      });
      toast.success(`Deleted ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"}`);
      await loadCampaign();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="meta-label">Master Exclusion List</div>
            <h3 className="font-display text-lg font-bold">Database-wide suppression ({masterTotal})</h3>
          </div>
          <Button onClick={loadMaster} variant="outline" className="rounded-sm h-9" data-testid="master-excl-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => exportMaster("csv")} variant="outline" className="rounded-sm h-9" data-testid="master-excl-export-csv">
            <Download size={14} className="mr-1" /> CSV
          </Button>
          <Button onClick={() => exportMaster("xlsx")} variant="outline" className="rounded-sm h-9" data-testid="master-excl-export-xlsx">
            <Download size={14} className="mr-1" /> XLSX
          </Button>
          <Button onClick={purgeMaster} variant="outline" disabled={masterTotal === 0} className="rounded-sm h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="master-excl-delete-all">
            <Trash2 size={14} className="mr-1" /> Delete All
          </Button>
        </div>
        <div>
          <UploadZone label="Upload master exclusion file" onUpload={uploadMaster} testid="master-excl-upload" />
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="meta-label">Campaign Exclusion List</div>
            <h3 className="font-display text-lg font-bold">Per-campaign suppression ({campTotal})</h3>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="camp-excl-filter"
              value={campFilter}
              onChange={(e) => setCampFilter(e.target.value)}
              className="rounded-sm h-9 px-2 border border-input bg-background text-sm"
            >
              <option value="">All campaigns</option>
              {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Button onClick={loadCampaign} variant="outline" className="rounded-sm h-9" data-testid="camp-excl-refresh">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            <Button onClick={() => exportCamp("csv")} variant="outline" className="rounded-sm h-9" data-testid="camp-excl-export-csv">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button onClick={() => exportCamp("xlsx")} variant="outline" className="rounded-sm h-9" data-testid="camp-excl-export-xlsx">
              <Download size={14} className="mr-1" /> XLSX
            </Button>
            <Button onClick={purgeCamp} variant="outline" disabled={campTotal === 0} className="rounded-sm h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="camp-excl-delete-all">
              <Trash2 size={14} className="mr-1" /> {campFilter ? "Delete in scope" : "Delete All"}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <select
            data-testid="camp-excl-target"
            value={campTarget}
            onChange={(e) => setCampTarget(e.target.value)}
            className="w-full md:w-1/2 rounded-sm h-9 px-2 border border-input bg-background text-sm"
          >
            <option value="">— Pick a campaign to upload into —</option>
            {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <UploadZone label={`Upload exclusion file${campTarget ? ` for ${campTarget}` : ""}`} onUpload={uploadCampaign} testid="camp-excl-upload" />
        </div>
      </section>

      <section data-testid="master-dnc-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="meta-label flex items-center gap-1.5"><Ban size={11} /> Master DNC List</div>
            <h3 className="font-display text-lg font-bold">Database-wide Do-Not-Contact ({masterDncTotal})</h3>
            <p className="text-xs text-muted-foreground mt-1">Separate from Master Exclusions — explicit opt-outs that must never be contacted.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadMasterDnc} variant="outline" className="rounded-sm h-9" data-testid="master-dnc-refresh">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            <Button onClick={() => exportMasterDnc("csv")} variant="outline" className="rounded-sm h-9" data-testid="master-dnc-export-csv">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button onClick={() => exportMasterDnc("xlsx")} variant="outline" className="rounded-sm h-9" data-testid="master-dnc-export-xlsx">
              <Download size={14} className="mr-1" /> XLSX
            </Button>
            <Button onClick={purgeMasterDnc} variant="outline" disabled={masterDncTotal === 0} className="rounded-sm h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="master-dnc-delete-all">
              <Trash2 size={14} className="mr-1" /> Delete All
            </Button>
          </div>
        </div>
        <UploadZone label="Upload master DNC file" onUpload={uploadMasterDnc} testid="master-dnc-upload" />
      </section>

      <section data-testid="campaign-dnc-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="meta-label flex items-center gap-1.5"><Ban size={11} /> Campaign DNC List</div>
            <h3 className="font-display text-lg font-bold">Per-campaign Do-Not-Contact ({campDncTotal})</h3>
            <p className="text-xs text-muted-foreground mt-1">Stored in its own database, parallel to Campaign Exclusions.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="camp-dnc-filter"
              value={campDncFilter}
              onChange={(e) => setCampDncFilter(e.target.value)}
              className="rounded-sm h-9 px-2 border border-input bg-background text-sm"
            >
              <option value="">All campaigns</option>
              {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Button onClick={loadCampaignDnc} variant="outline" className="rounded-sm h-9" data-testid="camp-dnc-refresh">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            <Button onClick={() => exportCampaignDnc("csv")} variant="outline" className="rounded-sm h-9" data-testid="camp-dnc-export-csv">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button onClick={() => exportCampaignDnc("xlsx")} variant="outline" className="rounded-sm h-9" data-testid="camp-dnc-export-xlsx">
              <Download size={14} className="mr-1" /> XLSX
            </Button>
            <Button onClick={purgeCampaignDnc} variant="outline" disabled={campDncTotal === 0} className="rounded-sm h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="camp-dnc-delete-all">
              <Trash2 size={14} className="mr-1" /> {campDncFilter ? "Delete in scope" : "Delete All"}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <select
            data-testid="camp-dnc-target"
            value={campDncTarget}
            onChange={(e) => setCampDncTarget(e.target.value)}
            className="w-full md:w-1/2 rounded-sm h-9 px-2 border border-input bg-background text-sm"
          >
            <option value="">— Pick a campaign to upload into —</option>
            {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <UploadZone label={`Upload DNC file${campDncTarget ? ` for ${campDncTarget}` : ""}`} onUpload={uploadCampaignDnc} testid="camp-dnc-upload" />
        </div>
      </section>
    </div>
  );
}
